import { Box, Switch, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { ETimePeriods } from "../../enums/timesheets.enum";
import { colors, alphaVar } from "../../../ui/colors";

interface DashboardHeaderProps {
  timePeriods: { label: string; value: ETimePeriods }[];
  value: ETimePeriods;
  onSetTimePeriod: (timePeriod: ETimePeriods) => void;
}

export const WorkfoceHeader = ({ timePeriods, value, onSetTimePeriod }: DashboardHeaderProps) => {
  const theme = useTheme();

  const getFullLabel = (v: ETimePeriods) => {
    switch (v) {
      case ETimePeriods.WEEKLY:
        return "Last 7 days";
      case ETimePeriods.MONTHLY:
        return "Last 30 days";
      case ETimePeriods.QUARTERLY:
        return "Last 90 days";
      case ETimePeriods.YEARLY:
        return "Last 365 days";
      default:
        return "";
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        gap: { xs: 2, md: 0 },
      }}
    >
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            letterSpacing: "-0.02em",
            mb: 0.5,
          }}
        >
          Workforce Overview
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "0.875rem",
          }}
        >
          Manage and view both your workforce and connections.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", md: "flex-end" },
          alignItems: "center",
          width: { xs: "100%", md: "auto" },
          mt: { xs: 1, md: 0 },
        }}
      >
        <ToggleButtonGroup
          value={value}
          onChange={(event, newValue) => {
            if (newValue !== null) {
              onSetTimePeriod(newValue);
            }
          }}
          exclusive
          sx={{
            display: "flex",
            p: 0.5,
            bgcolor: alphaVar(theme.palette.grey[100], 0.6),
            borderRadius: "12px",
            gap: 0.5,
            border: "none",
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              "&.Mui-disabled": {
                border: 0,
              },
              "&:not(:first-of-type)": {
                borderRadius: "10px",
              },
              "&:first-of-type": {
                borderRadius: "10px",
              },
            },
          }}
        >
          {timePeriods.map((period) => {
            const isSelected = value === period.value;
            return (
              <ToggleButton
                key={period.value}
                value={period.value}
                sx={{
                  px: isSelected ? 2.5 : 2,
                  py: 1,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "none",
                  color: theme.palette.text.secondary,
                  cursor: "pointer",
                  borderRadius: "10px !important",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: "none",
                  whiteSpace: "nowrap",
                  "&.Mui-selected": {
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    color: colors.orange[400],
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
                    "&:hover": {
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    },
                  },
                  "&:hover": {
                    bgcolor: alphaVar(theme.palette.common.white, 0.5),
                    color: colors.orange[400],
                  },
                }}
              >
                {isSelected ? getFullLabel(period.value) : period.label}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};
